import React from 'react';
import style from '../../assets/styles/KitchenTablesDashboard.module.scss';

// Normaliza o número da mesa (remove "Mesa", espaços, etc.)
export const normalizeTableNumber = (value) => {
  if (value === undefined || value === null) return null;
  const str = String(value).trim();
  if (!str) return null;
  const cleaned = str.replace(/^mesa\s*/i, '').trim();
  return cleaned || str;
};

// Converte string de data para timestamp (ms) com suporte a formatos brasileiros e ISO
export const parseDateToMs = (str) => {
  if (!str) return 0;
  if (typeof str === 'number') return str;
  if (str instanceof Date) return str.getTime();
  try {
    const rawStr = String(str).trim();
    // Se for formato ISO 8601
    const directParsed = Date.parse(rawStr);
    if (!isNaN(directParsed) && !rawStr.includes('/')) {
      return directParsed;
    }
    // Formato brasileiro "DD/MM/YYYY - HH:mm:ss" ou "DD/MM/YYYY HH:mm:ss"
    const normalized = rawStr.replace(' - ', ' ').trim();
    const [datePart, timePart] = normalized.split(' ');
    if (datePart && datePart.includes('/')) {
      const [day, month, year] = datePart.split('/');
      const [hour, minute, second] = (timePart || '00:00:00').split(':');
      const d = new Date(
        Number(year),
        Number(month) - 1,
        Number(day),
        Number(hour || 0),
        Number(minute || 0),
        Number(second || 0)
      );
      if (!isNaN(d.getTime())) return d.getTime();
    }
    return !isNaN(directParsed) ? directParsed : 0;
  } catch (e) {
    return 0;
  }
};

// Limpa qualquer chave residual legada de localStorage para não poluir sessões novas
try {
  localStorage.removeItem('kitchen_completed_initial_tables');
} catch (e) {}

/**
 * Calcula status, cor e peso de prioridade da mesa
 * Regras estritas da cozinha:
 * - Azul: Chamando garçom (máxima urgência)
 * - Verde: Pediu a conta
 * - Vermelha: Fez o pedido inicialmente e tem itens para serem entregues.
 *             CONTINUA VERMELHA mesmo que alguns itens já tenham sido entregues.
 *             Só sai do vermelho quando TODOS os itens iniciais forem entregues.
 *             Regra de ouro: "Enquanto não esvaziar o pedido inicial, não deixa a cor vermelha."
 * - Branca: Quando TODOS os itens da mesa forem entregues / despachados pela cozinha.
 * - Laranja: Se após ter tudo do primeiro lote entregue (estando branca), o cliente pedir algo mais.
 */
export const getKitchenTableStatus = (ordersForTable, tableKey = null, completedSet = null) => {
  // 1. Azul: Chamando garçom
  const hasWaiterCall = ordersForTable.some(
    (o) => o.waiterCall && o.waiterCall.active === true
  );
  if (hasWaiterCall) {
    return {
      status: 'blue',
      className: `${style.tableBlue} ${style.tableBlueBlinking}`,
      label: 'Chamou o garçom',
      priorityWeight: 1,
    };
  }

  // 2. Verde: Pediu a conta
  const hasActiveBillCall = ordersForTable.some(
    (o) => o.paymentCall && o.paymentCall.active === true
  );
  if (hasActiveBillCall) {
    return {
      status: 'green',
      className: `${style.tableGreen} ${style.tableGreenBlinking}`,
      label: 'Pediu a conta',
      priorityWeight: 2,
    };
  }
  const hasPendingBill = ordersForTable.some(
    (o) => Boolean(o.paymentCall) && (!o.paymentDone || !o.orderDelivered)
  );
  if (hasPendingBill) {
    return {
      status: 'green',
      className: style.tableGreen,
      label: 'Conta solicitada',
      priorityWeight: 2,
    };
  }

  // Coleta todos os itens de prato dessa mesa com seus metadados temporais
  const itemsWithTime = ordersForTable.flatMap((order) => {
    const orderTimeMs = parseDateToMs(order.dateTime || order.createdAt);
    const requests = Array.isArray(order.request) ? order.request : [];
    return requests.map((item, idx) => {
      const itemTimeMs = item.sentToKitchenTime
        ? parseDateToMs(item.sentToKitchenTime)
        : orderTimeMs;
      const isDelivered = Boolean(item.entregue || item.kitchenDelivered);
      return {
        item,
        order,
        orderTimeMs,
        itemTimeMs: itemTimeMs || orderTimeMs,
        isDelivered,
        indexInRequest: item.indexInRequest ?? idx,
      };
    });
  });

  if (itemsWithTime.length === 0) {
    return {
      status: 'red',
      className: style.tableRed,
      label: 'Novo pedido / em preparo',
      priorityWeight: 3,
    };
  }

  const pendingItems = itemsWithTime.filter((x) => !x.isDelivered);

  // 3. Se TODOS os itens dessa mesa já foram entregues / feitos -> BRANCA
  if (pendingItems.length === 0) {
    if (tableKey && completedSet && !completedSet.has(tableKey)) {
      completedSet.add(tableKey);
    }
    return {
      status: 'white',
      className: style.tableDelivered,
      label: 'Todos os pratos entregues',
      priorityWeight: 5,
    };
  }

  // Identifica os itens do lote inicial da mesa.
  // O lote inicial é formado pelos itens com o menor timestamp de envio (com margem de 10 segundos).
  const validTimestamps = itemsWithTime
    .map((x) => x.itemTimeMs)
    .filter((t) => t > 0);
  const minTimeMs = validTimestamps.length > 0 ? Math.min(...validTimestamps) : 0;

  const initialBatchItems = minTimeMs > 0
    ? itemsWithTime.filter((x) => x.itemTimeMs <= minTimeMs + 10000)
    : itemsWithTime;

  const isInitialBatchFullyDelivered =
    initialBatchItems.length > 0 &&
    initialBatchItems.every((x) => x.isDelivered);

  // 4. Se o lote inicial AINDA NÃO teve todos os seus itens entregues:
  // A mesa DEVE permanecer estritamente VERMELHA.
  // Regra de ouro da cozinha: "Enquanto não esvaziar o pedido inicial, não deixa a cor vermelha."
  // Aplica-se de forma idêntica tanto para pedidos do PDV quanto para pedidos diretos da mesa do cliente.
  if (!isInitialBatchFullyDelivered) {
    return {
      status: 'red',
      className: style.tableRed,
      label: 'Pedido inicial em preparo (todos devem ser entregues)',
      priorityWeight: 3,
    };
  }

  // 5. Laranja: Apenas se TODOS os itens do pedido inicial já foram entregues (a mesa ficou limpa/branca),
  // e posteriormente o cliente pediu algo mais que ainda está pendente.
  return {
    status: 'orange',
    className: style.tableOrange,
    label: 'Novo pedido após entregas iniciais',
    priorityWeight: 4,
  };
};

const KitchenTablesDashboard = ({
  requestsDoneList = [],
  selectedTableFilter = null,
  onSelectTable,
  onClearFilter,
}) => {
  // 1. Agrupar pedidos por mesa e pedidos pré-pagos
  const { tableOrdersMap, prePagoOrders } = React.useMemo(() => {
    const map = new Map();
    const prepago = [];

    requestsDoneList.forEach((order) => {
      const rawTable = order.tableNumber || order.mesa;
      const table = normalizeTableNumber(rawTable);

      if (table) {
        if (!map.has(table)) {
          map.set(table, []);
        }
        map.get(table).push(order);
      } else {
        prepago.push(order);
      }
    });

    return { tableOrdersMap: map, prePagoOrders: prepago };
  }, [requestsDoneList]);

  // 2. Conjunto em memória para rastrear mesas com ciclo inicial concluído na sessão ativa
  const completedSetRef = React.useRef(new Set());
  const completedSet = completedSetRef.current;

  // Limpa do completedSet as mesas que não estão mais ativas no salão
  React.useEffect(() => {
    const activeKeys = new Set(tableOrdersMap.keys());
    completedSet.forEach((tbl) => {
      if (!activeKeys.has(tbl)) {
        completedSet.delete(tbl);
      }
    });
  }, [tableOrdersMap, completedSet]);

  // 3. Construir objetos de mesa com status, cor, peso e timestamp
  const tableEntries = [];

  tableOrdersMap.forEach((orders, table) => {
    const statusInfo = getKitchenTableStatus(orders, table, completedSet);

    // Timestamp mais recente da mesa (para empurrar recém-chegados para a esquerda)
    const timestamps = orders.map((o) => parseDateToMs(o.dateTime || o.createdAt));
    const newestTimestamp = timestamps.length > 0 ? Math.max(...timestamps) : 0;

    tableEntries.push({
      table,
      orders,
      statusInfo,
      newestTimestamp,
      priorityWeight: statusInfo.priorityWeight,
    });
  });

  // 4. Hierarquia de ordenação:
  // - Hierarquia de cor/prioridade: Azul/Verde (1, 2) e Vermelhas (3) vêm à esquerda de Laranja (4) e Branca (5).
  // - Recém-chegados entram na esquerda: dentro de mesma prioridade, timestamp decrescente (mais recente à esquerda).
  tableEntries.sort((a, b) => {
    if (a.priorityWeight !== b.priorityWeight) {
      return a.priorityWeight - b.priorityWeight;
    }
    return b.newestTimestamp - a.newestTimestamp;
  });

  if (tableEntries.length === 0 && prePagoOrders.length === 0) {
    return null;
  }

  const selectedDisplay =
    selectedTableFilter && selectedTableFilter.startsWith('prepago-')
      ? 'Pedido Balcão / Pré-Pago'
      : selectedTableFilter
      ? `Mesa ${
          !isNaN(parseInt(selectedTableFilter, 10)) && parseInt(selectedTableFilter, 10) < 10
            ? String(selectedTableFilter).padStart(2, '0')
            : selectedTableFilter
        }`
      : null;

  return (
    <div className={style.tablesDashboardSection}>
      <div className={style.dashboardHeader}>
        <h3 className={style.sectionTitle}>
          🍽️ Mesas em Atendimento
        </h3>
        <span className={style.tablesCount}>
          {tableEntries.length} mesa{tableEntries.length !== 1 ? 's' : ''}
          {prePagoOrders.length > 0 ? ` • ${prePagoOrders.length} balcão/pré-pago` : ''}
        </span>
      </div>

      <div className={style.tablesGrid}>
        {tableEntries.map(({ table, orders, statusInfo }) => {
          const isSelected = selectedTableFilter === table;
          const displayTableNum =
            !isNaN(parseInt(table, 10)) && parseInt(table, 10) < 10
              ? String(table).padStart(2, '0')
              : table;

          return (
            <button
              type="button"
              key={`kitchen-table-${table}`}
              data-testid={`kitchen-table-btn-${table}`}
              className={`${style.tableSquare} ${statusInfo.className} ${
                isSelected ? style.tableSelected : ''
              }`}
              onClick={() => onSelectTable(table)}
              title={`Mesa ${table} - ${statusInfo.label} (${orders.length} pedido${
                orders.length > 1 ? 's' : ''
              })`}
              aria-pressed={isSelected}
            >
              {displayTableNum}
            </button>
          );
        })}

        {/* Pedidos Pré-Pagos / Balcão (se existirem) */}
        {prePagoOrders.map((order) => {
          const prepagoKey = `prepago-${order.id}`;
          const isSelected = selectedTableFilter === prepagoKey;
          const displayNum = order.countRequest
            ? `#${order.countRequest}`
            : `#${order.id.slice(-4)}`;

          return (
            <button
              type="button"
              key={prepagoKey}
              data-testid={`kitchen-prepago-btn-${order.id}`}
              className={`${style.prePagoSquare} ${
                isSelected ? style.tableSelected : ''
              }`}
              onClick={() => onSelectTable(prepagoKey)}
              title={`Balcão / Pré-Pago ${displayNum} - Cliente: ${
                order.name || 'Cliente'
              }`}
              aria-pressed={isSelected}
            >
              {displayNum}
            </button>
          );
        })}
      </div>

      {/* Barra de Limpar Filtro */}
      {selectedTableFilter && (
        <div className={style.clearSelectionBar}>
          <span className={style.selectionInfoText}>
            Exibindo apenas: <strong>{selectedDisplay}</strong>
          </span>
          <button
            type="button"
            data-testid="kitchen-clear-filter-btn"
            className={style.btnClearSelection}
            onClick={onClearFilter}
          >
            ✕ Limpar filtro (Ver todas as mesas)
          </button>
        </div>
      )}
    </div>
  );
};

export default KitchenTablesDashboard;
